import React from 'react';
import { CharacterData, ComputedStats } from '../types/character';
import { CharacterStatComputer } from './CharacterStatComputer';
import { ExperienceEntry } from '../types/levelUp';

// Common interface for enhanced character used across components
export interface EnhancedCharacter {
  character: CharacterData | null;
  computedStats: ComputedStats | null;
  statComputer: CharacterStatComputer | null;
  activateClassFeature: (featureId: string) => void;
  deactivateClassFeature: (featureId: string) => void;
  // Direct character update methods
  updateCharacterInfo?: (updates: Partial<Pick<CharacterData, 'name' | 'class' | 'heritage' | 'level' | 'tier'>>) => void;
  updateBaseStats?: (updates: Partial<CharacterData['baseStats']>) => void;
  updateCurrentState?: (updates: Partial<CharacterData['currentState']>) => void;
  updateManualOverrides?: (updates: Partial<NonNullable<CharacterData['manualOverrides']>>) => void;
  updateClassSpecificData?: (updates: Partial<CharacterData['classSpecificData']>) => void;
  updateExperiences?: (experiences: ExperienceEntry[]) => void;
  updateInventoryManualItems?: (manualItems: any[]) => void;
  // Reference-based equipment operations
  addEquipmentByReference?: (itemId: string, quantity?: number) => void;
  removeEquipmentByReference?: (itemId: string, quantity?: number) => void;
  equipItemByReference?: (itemId: string, count?: number) => void;
  unequipItemByReference?: (itemId: string, count?: number) => void;
  // Loot & Consumable management
  addLootByReference?: (itemId: string, quantity?: number) => void;
  removeLootByReference?: (itemId: string, quantity?: number) => void;
  updateLootQuantity?: (itemId: string, newQuantity: number) => void;
  addConsumableByReference?: (itemId: string, quantity?: number) => void;
  removeConsumableByReference?: (itemId: string, quantity?: number) => void;
  updateConsumableQuantity?: (itemId: string, newQuantity: number) => void;
  // Vault & Loadout System (Phase 48)
  addDomainCardToLoadout?: (cardId: string, autoVault?: boolean) => void;
  moveDomainCardToVault?: (cardId: string) => void;
  moveDomainCardToLoadout?: (cardId: string) => void;
  removeDomainCard?: (cardId: string) => void;
}

// Interface for class-specific section components
export interface ClassSectionComponent {
  component: React.ComponentType<{
    enhancedCharacter: EnhancedCharacter;
  }>;
  title: string;
  priority: 'high' | 'medium' | 'low';
  defaultExpanded: boolean;
  order: number; // For controlling section order
  selfContained?: boolean; // If true, component already includes CollapsibleSection wrapper
}

// Interface for class-specific combat components
export interface ClassCombatComponent {
  component: React.ComponentType<{
    enhancedCharacter: EnhancedCharacter;
  }>;
  order: number; // For controlling component order in combat tracker
}

// Interface for class provider
export interface ClassProvider {
  className: string;
  characterSheetSections: ClassSectionComponent[];
  combatTrackerComponents: ClassCombatComponent[];
  
  // Optional: Custom validation or initialization logic
  validateCharacter?: (character: CharacterData) => boolean;
  initializeCharacter?: (character: CharacterData) => Partial<CharacterData>;
}

// Registry for all class providers
class ClassComponentRegistryClass {
  private providers: Map<string, ClassProvider> = new Map();

  /**
   * Register a class provider with the registry
   */
  register(provider: ClassProvider): void {
    const normalizedClassName = provider.className.toLowerCase();
    this.providers.set(normalizedClassName, provider);
  }

  /**
   * Get character sheet sections for a specific class
   */
  getCharacterSheetSections(className: string): ClassSectionComponent[] {
    const normalizedClassName = className.toLowerCase();
    const provider = this.providers.get(normalizedClassName);
    
    
    return provider?.characterSheetSections || [];
  }

  /**
   * Get combat tracker components for a specific class
   */
  getCombatTrackerComponents(className: string): ClassCombatComponent[] {
    const normalizedClassName = className.toLowerCase();
    const provider = this.providers.get(normalizedClassName);
    return provider?.combatTrackerComponents || [];
  }

  /**
   * Check if a class is registered
   */
  isClassRegistered(className: string): boolean {
    const normalizedClassName = className.toLowerCase();
    return this.providers.has(normalizedClassName);
  }

  /**
   * Get all registered class names
   */
  getRegisteredClasses(): string[] {
    return Array.from(this.providers.keys());
  }

  /**
   * Validate character for a specific class
   */
  validateCharacter(className: string, character: CharacterData): boolean {
    const normalizedClassName = className.toLowerCase();
    const provider = this.providers.get(normalizedClassName);
    
    if (!provider) {
      return true; // No validation required if class not registered
    }
    
    return provider.validateCharacter ? provider.validateCharacter(character) : true;
  }

  /**
   * Initialize character data for a specific class
   */
  initializeCharacter(className: string, character: CharacterData): Partial<CharacterData> {
    const normalizedClassName = className.toLowerCase();
    const provider = this.providers.get(normalizedClassName);
    
    if (!provider || !provider.initializeCharacter) {
      return {};
    }
    
    return provider.initializeCharacter(character);
  }

  /**
   * Clear all registered providers (mainly for testing)
   */
  clear(): void {
    this.providers.clear();
  }
}

// Export singleton instance
export const ClassComponentRegistry = new ClassComponentRegistryClass();

// Helper function to sort components by order
export const sortByOrder = <T extends { order: number }>(components: T[]): T[] => {
  return [...components].sort((a, b) => a.order - b.order);
};